import { ReactNode, useEffect, useState } from 'react';
import InstallPromptLocation from '@alltrails/amplitude/enums/InstallPromptLocation';
import logInstallPromptDismissed from '@alltrails/amplitude/events/logInstallPromptDismissed';
import logInstallPromptViewed from '@alltrails/amplitude/events/logInstallPromptViewed';
import useIsMobileSizedScreen from '@alltrails/denali/hooks/useIsMobileSizedScreen';
import { useExperiments, useExposureEvent } from '@alltrails/experiments';
import { Modal } from '@alltrails/modal';
import styles from './BaseInstallInterstitial.module.scss';
import InstallPromptTrigger from '@alltrails/amplitude/enums/InstallPromptTrigger';

type BaseInstallInterstitialProps = {
  isOpen?: boolean;
  onRequestClose?: () => void;
  promptLocation: InstallPromptLocation;
  displayTrigger?: InstallPromptTrigger;
  children: ReactNode;
  hidePageScrollbarGutter?: boolean;
  isDeviceHeight?: boolean;
  shouldHideCloseButton?: boolean;
  clickOutsideDeactivates?: boolean;
};

const BaseInstallInterstitial = ({
  isOpen: isOpenAttempt = false,
  onRequestClose,
  promptLocation,
  displayTrigger,
  children,
  hidePageScrollbarGutter = false,
  isDeviceHeight = false,
  shouldHideCloseButton = false,
  clickOutsideDeactivates = true
}: BaseInstallInterstitialProps) => {
  const experiments = useExperiments();
  const isAppsFlyerEnabled = experiments['web-appsflyer-migration']?.value === 'on';
  const isMobile = useIsMobileSizedScreen();
  const [isMounted, setIsMounted] = useState(false);
  const isOpen = isMounted && isOpenAttempt && isMobile;
  const sendExposure = useExposureEvent('web-appsflyer-migration');

  useEffect(() => {
    if (isOpen && isMobile) {
      sendExposure();
    }
  }, [isOpen, isMobile, sendExposure]);

  useEffect(() => {
    setIsMounted(true);
  }, []);

  useEffect(() => {
    if (isOpen) {
      logInstallPromptViewed({
        install_prompt_location: promptLocation,
        install_prompt_type: 'interstitial',
        install_prompt_source: isAppsFlyerEnabled ? 'native_with_appsflyer_link' : 'native',
        install_prompt_trigger: displayTrigger
      });
    }
  }, [isAppsFlyerEnabled, isOpen, promptLocation, displayTrigger]);

  const handleCloseClick = () => {
    logInstallPromptDismissed({
      install_prompt_location: promptLocation,
      install_prompt_type: 'interstitial',
      install_prompt_trigger: displayTrigger,
      install_prompt_source: isAppsFlyerEnabled ? 'native_with_appsflyer_link' : 'native'
    });
    onRequestClose?.();
  };

  return (
    <Modal
      contentsClassName={styles.contents}
      focusTrapOptions={{ clickOutsideDeactivates }}
      hidePageScrollbarGutter={hidePageScrollbarGutter}
      isDeviceHeight={isDeviceHeight}
      isOpen={isOpen}
      onCloseRequest={handleCloseClick}
      shouldHideCloseButton={shouldHideCloseButton}
    >
      {children}
    </Modal>
  );
};

export default BaseInstallInterstitial;
